import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { CircularProgress } from "@mui/material";
import { getToken } from "../../services/Token";
import engageApi, { useGetEngageFeedQuery } from "../../api/engageApi";
import GiveKudos from "./GiveKudos";
import CreatePoll from "./CreatePoll";
import KudosCard from "./KudosCard";
import PollCard from "./PollCard";
import CelebrationCard from "./CelebrationCard";
import AnnouncementCard from "./AnnouncementCard";
import CreateAnnouncement from "./CreateAnnouncement";
import EngageFilters from "./EngageFilters";
import {
  DEFAULT_FEED_FILTERS,
  POST_TYPE,
  canComposeAnnouncement,
  hasActiveFilters,
  isCelebration,
  normalizePost,
} from "./constants";
import AdminTriggerMails from "./AdminTriggerMails";

const PAGE_SIZE = 10;

// One route for every audience: any employee can give kudos or run a poll. The
// only role-dependent piece will be the Announcement composer (ADMIN only).
const Engage = () => {
  const dispatch = useDispatch();
  // Announcements are the one ADMIN-only composer; kudos and polls are open to
  // everyone, so this gates the menu entry and nothing else on the screen.
  const canAnnounce = canComposeAnnouncement(getToken("roles"));
  const [view, setView] = useState("FEED");
  const [menuOpen, setMenuOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ ...DEFAULT_FEED_FILTERS });

  const feedArgs = useMemo(
    () => ({
      page,
      size: PAGE_SIZE,
      sort: filters.sort,
      activity: filters.activity,
      audienceId: filters.audienceId,
    }),
    [page, filters]
  );

  // Any filter change has to reset the page, or you land on page 3 of a result
  // set that now has one page and the feed comes back empty.
  const applyFilters = (next) => {
    setPage(0);
    setFilters(next);
  };
  const { data, isLoading, isFetching, isError, refetch } =
    useGetEngageFeedQuery(feedArgs);

  const posts = useMemo(
    () => (data?.items || []).map(normalizePost).filter(Boolean),
    [data]
  );

  const totalPages = data?.totalPages || 0;
  const currentPage = data?.currentPage || 0;

  const patchPost = (postId, apply) => {
    dispatch(
      engageApi.util.updateQueryData("getEngageFeed", feedArgs, (draft) => {
        const row = (draft.items || []).find((item) => item.id === postId);
        if (row) apply(row);
      })
    );
  };

  const handleReacted = (postId, reactions) => {
    patchPost(postId, (row) => {
      row.reactions = reactions;
    });
  };

  const handleVoted = (postId, poll) => {
    patchPost(postId, (row) => {
      row.poll = poll;
    });
  };

  const goToFeed = () => {
    setPage(0);
    setView("FEED");
  };

  const filtered = hasActiveFilters(filters);

  if (view === "KUDOS") {
    return <GiveKudos onBack={() => setView("FEED")} onPosted={goToFeed} />;
  }

  if (view === "POLL") {
    return <CreatePoll onBack={() => setView("FEED")} onPosted={goToFeed} />;
  }

  if (view === "SEND_MAIL") {
    return <AdminTriggerMails onBack={() => setView("FEED")} />;
  }

  if (view === "ANNOUNCEMENT") {
    return (
      <CreateAnnouncement onBack={() => setView("FEED")} onPosted={goToFeed} />
    );
  }

  const renderCard = (post) => {
    if (post.type === POST_TYPE.POLL) {
      return <PollCard key={post.id} post={post} onVoted={handleVoted} />;
    }
    if (post.type === POST_TYPE.ANNOUNCEMENT) {
      return (
        <AnnouncementCard
          key={post.id}
          post={post}
          onReacted={handleReacted}
        />
      );
    }
    if (isCelebration(post.type)) {
      return (
        <CelebrationCard
          key={post.id}
          post={post}
          onReacted={handleReacted}
        />
      );
    }
    return <KudosCard key={post.id} post={post} onReacted={handleReacted} />;
  };

  return (
    <div className="p-6" onClick={() => setMenuOpen(false)}>
      <div className="mx-auto max-w-3xl">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Engage</h1>
            <p className="text-sm text-gray-500">
              Celebrations, recognition and polls across the company
            </p>
          </div>

          <div
            className="relative flex-none"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className="rounded-md bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              + Create
            </button>

            {menuOpen && (
              <div className="absolute right-0 z-20 mt-2 w-56 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg">
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setView("KUDOS");
                  }}
                  className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left hover:bg-gray-50"
                >
                  <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-pink-50 text-sm">
                    &#127894;
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-gray-800">
                      Give Kudos
                    </span>
                    <span className="block text-xs text-gray-400">
                      Recognize a colleague
                    </span>
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setView("POLL");
                  }}
                  className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left hover:bg-gray-50"
                >
                  <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-teal-50 text-sm">
                    &#128202;
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-gray-800">
                      Create Poll
                    </span>
                    <span className="block text-xs text-gray-400">
                      Ask the team a question
                    </span>
                  </span>
                </button>

                {canAnnounce && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        setView("ANNOUNCEMENT");
                      }}
                      className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left hover:bg-gray-50"
                    >
                      <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-amber-50 text-sm">
                        &#128227;
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-gray-800">
                          Announcement
                        </span>
                        <span className="block text-xs text-gray-400">
                          Share news with a group
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        setView("SEND_MAIL");
                      }}
                      className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left hover:bg-gray-50"
                    >
                      <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-amber-50 text-sm">
                        &#9993;
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-gray-800">
                          Send Email
                        </span>
                        <span className="block text-xs text-gray-400">
                          Email selected employees
                        </span>
                      </span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <EngageFilters
          filters={filters}
          onChange={applyFilters}
          disabled={isLoading}
        />

        {isLoading && (
          <div className="flex justify-center py-16">
            <CircularProgress size={30} />
          </div>
        )}

        {isError && !isLoading && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-6 py-10 text-center">
            <p className="text-sm font-semibold text-red-700">
              The feed could not be loaded
            </p>
            <button
              type="button"
              onClick={refetch}
              className="mt-4 rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
            >
              Try again
            </button>
          </div>
        )}

        {!isLoading && !isError && posts.length === 0 && filtered && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-14 text-center">
            <p className="text-sm font-semibold text-gray-600">
              No posts match these filters
            </p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-gray-400">
              There may still be posts in other groups or activities.
            </p>
            <button
              type="button"
              onClick={() => applyFilters({ ...DEFAULT_FEED_FILTERS })}
              className="mt-5 rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Clear filters
            </button>
          </div>
        )}

        {!isLoading && !isError && posts.length === 0 && !filtered && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-14 text-center">
            <p className="text-sm font-semibold text-gray-600">
              Nothing in the feed yet
            </p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-gray-400">
              Birthdays and work anniversaries appear here automatically. Kudos
              and announcements go to everyone; a poll goes only to the group
              you choose.
            </p>
            <div className="mt-5 flex justify-center gap-2">
              <button
                type="button"
                onClick={() => setView("KUDOS")}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Give Kudos
              </button>
              <button
                type="button"
                onClick={() => setView("POLL")}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Create Poll
              </button>
            </div>
          </div>
        )}

        {!isLoading && !isError && posts.length > 0 && (
          <div
            className={
              isFetching
                ? "flex flex-col gap-3 opacity-60 transition-opacity"
                : "flex flex-col gap-3"
            }
          >
            {posts.map(renderCard)}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <button
              type="button"
              disabled={currentPage <= 0 || isFetching}
              onClick={() => setPage((value) => Math.max(0, value - 1))}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-xs text-gray-400">
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage + 1 >= totalPages || isFetching}
              onClick={() => setPage((value) => value + 1)}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Engage;
